import appconfig from '@/appconfig';
import { nanoid } from 'nanoid';

// to use the more convient cam case notation in css objects like
// backgroundColor: #333 we need to decase while setting it via
// setProperty.
// Object.assign works with cameCase values but ignores !important
const deCase = (s: string) =>
  s.replace(/[A-Z]/g, function (a) {
    return '-' + a.toLowerCase();
  });

// maybe use nanoID instead
// from: https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid/2117523#2117523

export const uuidv4 = () => nanoid();
/* ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
  (
    c ^
    (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
  ).toString(16), */
/* ); */

export const setStyles = function (target: HTMLElement, stylesObject: Partial<CSSStyleDeclaration>) {
  Object.entries(stylesObject).forEach(([key, value]) => {
    const property = deCase(key);
    const hasImportant = `${value}`.includes('!important');
    const val = `${value}`.replace('!important', '');
    target.style.setProperty(property, val, hasImportant ? 'important' : '');
  });
};

export const setCSSVariables = function (target: CSSStyleDeclaration, vars: Record<string, string>) {
  Object.entries(vars).forEach((item) => {
    const [name, value] = item;
    target.setProperty(name, value);
  });
};

export const loadScript = (scriptSrc: string) =>
  new Promise<void>((resolve) => {
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.onload = () => {
      resolve();
    };
    document.head.appendChild(script);
  });

export const deepCopy = function (obj: object) {
  // the better solution whould be
  // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
  // but the support is not broad enough
  // please change in future, when the support is suitable
  return JSON.parse(JSON.stringify(obj));
};

export const isString = (str: unknown): str is string =>
  Object.prototype.toString.call(str) === '[object String]';

export const isObject = (obj: unknown): obj is Record<string, unknown> =>
  Object.prototype.toString.call(obj) === '[object Object]';

// https://github.com/Tokimon/vanillajs-helpers/blob/master/src/isBoolean.ts
/**
 * Is the given argument a boolean
 *
 * @example
 * ```ts
 * isBoolean(false); // -> true
 * isBoolean('string'); // -> false
 * ```
 *
 * @param x - Argument to test
 * @return - Whether the argument a Boolean or not
 */
export function isBoolean(x: unknown): x is boolean {
  return x === true || x === false || x instanceof Boolean;
}


export const getPrimusUrl = () => appconfig.botPath;

interface FileUploadHeader {
  name: string;
  value: string;
}
export function uploadFile(url: string, file: File, header: FileUploadHeader[], progressCallback: (progress: number) => void):
  Promise<{ status: number; body: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      const progress = Math.round((e.loaded / e.total) * 100);
      progressCallback(progress >= 100 ? 99 : progress);
    });
    xhr.addEventListener('load', () => {
      progressCallback(100);
      resolve({ status: xhr.status, body: xhr.responseText });
    });
    xhr.addEventListener('error', () =>
      reject(new Error('File upload failed')),
    );
    xhr.addEventListener('abort', () =>
      reject(new Error('File upload aborted')),
    );
    xhr.open('POST', url, true);
    header.forEach((h: FileUploadHeader) => {
      xhr.setRequestHeader(h.name, h.value);
    });
    const formData = new FormData();
    formData.append('file', file, file.name);
    xhr.send(formData);
  });
}


const getFileName = (parsedUrl: URL) => {
  const filenameParam = parsedUrl.searchParams.get(
    'response-content-disposition',
  ); // Userlike case
  if (!filenameParam) return;

  const fileName = filenameParam.split('=').pop(); // Example of param: "attachment; filename=Some pfd file.pdf"
  return fileName;
};

export const getFilenameFromURL = (url: string) => {
  try {
    const parsedURL = new URL(url);
    const fileNameFromParam = getFileName(parsedURL);
    if (fileNameFromParam) return fileNameFromParam;

    const urlParts = parsedURL.pathname.split('/');
    return urlParts[urlParts.length - 1];
  } catch {
    return null;
  }
};
